// Firefly Lab — dev-only sandbox for iterating on the firefly visual
// design without touching production surfaces. See
// docs/planning/firefly_visual_design.md for the multi-session
// iteration plan.
//
// F3 (2026-06-17): anchor + ambient hover model — drag to set anchor,
// Highlight flickers away and returns; V3 pulse curves + Critical jitter.
// F5 (2026-06-17): V9 reduced-motion — soft crossfades, static bug.
// F6 (2026-06-17): V5 auto-dwell — highlight on dwellDurationMs timer.
// Imports production modules from activity/firefly/ (V10 lift).

import { useCallback, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { Firefly } from "@/components/activity/firefly/Firefly";
import {
  applyFireflySpeedMultiplier,
  getFireflyIntentParams,
  type FireflyIntent,
} from "@/components/activity/firefly/fireflyIntents";
import { usePrefersReducedMotion } from "@/components/activity/firefly/usePrefersReducedMotion";
import { useFireflyAutoDwell } from "@/components/activity/firefly/useFireflyAutoDwell";
import { effectiveFireflyDwellMs } from "@/components/activity/firefly/fireflyMotion";
import { useFireflyMotionSpeedMultiplier } from "@/components/activity/firefly/useFireflyMotionSpeedMultiplier";

const VIEWPORT_W = 320;
const VIEWPORT_H = 180;
const EDGE_INSET = 32;

const INTENTS: readonly FireflyIntent[] = [
  "discovery",
  "teaching",
  "guidance",
  "focus",
  "critical",
];

const INTENT_BLURB: Record<FireflyIntent, string> = {
  discovery: "ambient, calm — \"I'm here\"",
  teaching: "eager — \"look here, useful\"",
  guidance: "standard — \"next step over here\"",
  focus: "quietest — \"won't interrupt you\"",
  critical: "insistent — \"look at me now\"",
};

const INTENT_COLOR_MEANING: Record<FireflyIntent, string> = {
  discovery: "Default ambient — blue (informational; low-urgency presence)",
  teaching: "Positive guidance — green (achievement / \"here's how to repeat it\")",
  guidance: "Standard attention — amber (needs review, next step when ready)",
  focus: "Ambient assistant — purple/magenta (available, not asking for anything)",
  critical: "Ops-urgent — red (broken route, customer at door, deal at risk)",
};

function clampAnchor(x: number, y: number): { x: number; y: number } {
  return {
    x: Math.min(VIEWPORT_W - EDGE_INSET, Math.max(EDGE_INSET, x)),
    y: Math.min(VIEWPORT_H - EDGE_INSET, Math.max(EDGE_INSET, y)),
  };
}

function initialAnchor(): { x: number; y: number } {
  return { x: VIEWPORT_W / 2, y: VIEWPORT_H / 2 };
}

function randomHighlightPoint(): { x: number; y: number } {
  return clampAnchor(
    EDGE_INSET + Math.random() * (VIEWPORT_W - 2 * EDGE_INSET),
    EDGE_INSET + Math.random() * (VIEWPORT_H - 2 * EDGE_INSET),
  );
}

export function DevPanelFireflyLabSection() {
  const [selectedIntent, setSelectedIntent] = useState<FireflyIntent>("discovery");
  const [anchor, setAnchor] = useState(initialAnchor);
  const [highlightGen, setHighlightGen] = useState(0);
  const [highlightPoint, setHighlightPoint] = useState(initialAnchor);
  const [highlightCount, setHighlightCount] = useState(0);
  const [autoHighlightCount, setAutoHighlightCount] = useState(0);
  const [forceReducedMotion, setForceReducedMotion] = useState(false);
  const [autoDwellEnabled, setAutoDwellEnabled] = useState(true);
  const [anchorDwellReady, setAnchorDwellReady] = useState(true);
  const [isDraggingAnchor, setIsDraggingAnchor] = useState(false);
  const systemReducedMotion = usePrefersReducedMotion();
  const [motionSpeedMultiplier] = useFireflyMotionSpeedMultiplier();
  const draggingRef = useRef(false);

  const reducedMotionActive = forceReducedMotion || systemReducedMotion;

  const params = useMemo(() => {
    const base = getFireflyIntentParams(selectedIntent);
    if (!base) return null;
    return applyFireflySpeedMultiplier(base, motionSpeedMultiplier);
  }, [selectedIntent, motionSpeedMultiplier]);

  const dwellMs = useMemo(() => {
    if (!params) return 0;
    const reduced = forceReducedMotion || systemReducedMotion;
    return effectiveFireflyDwellMs(
      params.dwellDurationMs,
      params,
      motionSpeedMultiplier,
      reduced,
    );
  }, [params, motionSpeedMultiplier, forceReducedMotion, systemReducedMotion]);

  const triggerHighlight = useCallback((source: "manual" | "auto" = "manual") => {
    const pt = randomHighlightPoint();
    setHighlightPoint(pt);
    setHighlightGen((g) => g + 1);
    if (source === "auto") {
      setAutoHighlightCount((c) => c + 1);
    } else {
      setHighlightCount((c) => c + 1);
    }
  }, []);

  useFireflyAutoDwell({
    enabled: autoDwellEnabled && dwellMs > 0,
    dwellMs,
    anchorDwellReady,
    paused: isDraggingAnchor,
    onDwellElapsed: () => triggerHighlight("auto"),
  });

  const onAnchorDwellReadyChange = useCallback((ready: boolean) => {
    setAnchorDwellReady(ready);
  }, []);

  const onViewportPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      draggingRef.current = true;
      setIsDraggingAnchor(true);
      e.currentTarget.setPointerCapture(e.pointerId);
      const rect = e.currentTarget.getBoundingClientRect();
      setAnchor(clampAnchor(e.clientX - rect.left, e.clientY - rect.top));
    },
    [],
  );

  const onViewportPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      const rect = e.currentTarget.getBoundingClientRect();
      setAnchor(clampAnchor(e.clientX - rect.left, e.clientY - rect.top));
    },
    [],
  );

  const onViewportPointerUp = useCallback(() => {
    draggingRef.current = false;
    setIsDraggingAnchor(false);
  }, []);

  return (
    <SectionCard
      defaultCollapsed
      title="Firefly Lab"
      description="Dev-only visual sandbox for the dock companion. Drag the viewport to set the anchor; Highlight flickers to a attention point and returns."
    >
      <p className="mb-3 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
        The firefly hovers calmly near its anchor (production: dock edge
        position). Drag inside the viewport to move the anchor. Highlight
        simulates an attention grab — flicker out, appear elsewhere, then
        return to anchor. With auto-dwell on, the firefly highlights on its
        own after each intent&apos;s dwell time (Critical ~1s, Focus ~5.3s at
        default 1.5× speed). Critical adds position jitter every ~2.7s.
      </p>

      <div
        role="tablist"
        aria-label="Firefly intent"
        className="mb-3 flex flex-wrap gap-1.5"
      >
        {INTENTS.map((intent) => {
          const intentParams = getFireflyIntentParams(intent);
          const selected = intent === selectedIntent;
          return (
            <button
              key={intent}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setSelectedIntent(intent)}
              className={[
                "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium capitalize transition-colors",
                selected
                  ? "border-gray-900 bg-gray-900 text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-zinc-900 dark:text-gray-200 dark:hover:bg-zinc-800",
              ].join(" ")}
            >
              <span
                aria-hidden
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/20"
                style={{
                  background: intentParams?.glowColor ?? "rgba(148, 163, 184, 0.6)",
                }}
              />
              {intent}
            </button>
          );
        })}
      </div>

      <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-700 dark:bg-zinc-900/60">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-800 dark:text-gray-200">
          {selectedIntent}
        </h4>
        <p className="mt-1 text-[11px] leading-snug text-gray-600 dark:text-gray-300">
          {INTENT_BLURB[selectedIntent]}
        </p>
        <p className="mt-1.5 text-[10px] leading-snug text-gray-500 dark:text-gray-400">
          {INTENT_COLOR_MEANING[selectedIntent]}
        </p>
        {params ? (
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] sm:grid-cols-5">
            <div>
              <dt className="text-gray-400">Pulse</dt>
              <dd className="font-mono tabular-nums text-gray-700 dark:text-gray-200">
                {params.pulseKind} {(params.pulseDurationMs / 1000).toFixed(1)}s
              </dd>
            </div>
            <div>
              <dt className="text-gray-400">Dwell</dt>
              <dd className="font-mono tabular-nums text-gray-700 dark:text-gray-200">
                {(params.dwellDurationMs / 1000).toFixed(1)}s
                {autoDwellEnabled && anchorDwellReady && !isDraggingAnchor ? (
                  <span className="ml-1 font-sans text-gray-400">· ticking</span>
                ) : null}
              </dd>
            </div>
            <div>
              <dt className="text-gray-400">Flicker</dt>
              <dd className="font-mono tabular-nums text-gray-700 dark:text-gray-200">
                {params.flickerDurationMs}ms
              </dd>
            </div>
            <div>
              <dt className="text-gray-400">Hover</dt>
              <dd className="font-mono tabular-nums text-gray-700 dark:text-gray-200">
                ±{params.hoverRadiusPx}px
              </dd>
            </div>
            <div>
              <dt className="text-gray-400">Jitter</dt>
              <dd className="font-mono tabular-nums text-gray-700 dark:text-gray-200">
                {params.jitterIntervalMs
                  ? `${params.jitterIntervalMs / 1000}s`
                  : "—"}
              </dd>
            </div>
          </dl>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div
          aria-label={`Firefly viewport for ${selectedIntent}`}
          onPointerDown={onViewportPointerDown}
          onPointerMove={onViewportPointerMove}
          onPointerUp={onViewportPointerUp}
          onPointerCancel={onViewportPointerUp}
          className="cursor-crosshair touch-none"
          style={{
            position: "relative",
            width: VIEWPORT_W,
            height: VIEWPORT_H,
            borderRadius: 10,
            background: "#0f172a",
            border: "1px solid #1e293b",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: anchor.x - 5,
              top: anchor.y - 5,
              width: 10,
              height: 10,
              borderRadius: 9999,
              border: "1px dashed rgba(148, 163, 184, 0.45)",
              pointerEvents: "none",
            }}
          />
          <Firefly
            intent={selectedIntent}
            anchorX={anchor.x}
            anchorY={anchor.y}
            highlightGeneration={highlightGen}
            highlightX={highlightPoint.x}
            highlightY={highlightPoint.y}
            forceReducedMotion={forceReducedMotion}
            onAnchorDwellReadyChange={onAnchorDwellReadyChange}
          />
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-[11px] text-gray-600 dark:text-gray-300">
            App motion speed:{" "}
            <span className="font-mono tabular-nums font-medium">
              {motionSpeedMultiplier.toFixed(1)}×
            </span>
            <span className="block text-[10px] text-gray-400">
              Set app-wide in Platform View → Overview (platform_owner).
            </span>
          </p>
          <label className="flex cursor-pointer items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={autoDwellEnabled}
              onChange={(e) => setAutoDwellEnabled(e.target.checked)}
              className="rounded border-gray-300"
            />
            Auto-dwell (V5)
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={forceReducedMotion}
              onChange={(e) => setForceReducedMotion(e.target.checked)}
              className="rounded border-gray-300"
            />
            Simulate reduced motion (V9)
          </label>
          {systemReducedMotion && !forceReducedMotion ? (
            <p className="text-[10px] text-amber-700 dark:text-amber-300">
              OS prefers-reduced-motion is on — firefly uses calm mode.
            </p>
          ) : null}
          {reducedMotionActive ? (
            <p className="text-[10px] leading-snug text-gray-500 dark:text-gray-400">
              Reduced motion: no pulse, hover drift, or jitter. Highlight
              uses soft crossfades; intent colors unchanged.
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => triggerHighlight("manual")}
            className="self-start rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200"
          >
            Highlight
          </button>
          <p className="text-[10px] tabular-nums text-gray-400">
            Manual: {highlightCount} · Auto: {autoHighlightCount}
          </p>
          <p className="max-w-xs text-[10px] leading-snug text-gray-500 dark:text-gray-400">
            Drag to reposition the anchor. Switch intent to compare colors and
            motion — Teaching flashes on entry; Critical uses heartbeat plus
            jitter (unless reduced motion is active).
          </p>
        </div>
      </div>
    </SectionCard>
  );
}
